'use client';

/**
 * PanelsLayout — region-driven layout for panels-as-cards presets.
 *
 * When a preset declares `panels` (instead of legacy `canvas_cards`),
 * this component renders the regions in flex order:
 *
 *   ┌─────────────── top region (sticky if flagged) ───────────────┐
 *   │  left   ║         viewport region          ║   right          │
 *   │  region ║         (scrollable)             ║   region         │
 *   ├──────────────── bottom region ───────────────────────────────┤
 *
 * The body row (left | viewport | right) is a `ResizablePanelGroup`
 * when a left and/or right region is present — drag the ║ handles to
 * resize, sizes persisted per preset (`autoSaveId` keyed off the
 * viewport content_key). With no side regions it's a plain full-width
 * viewport (no handles). Each region is itself a card region — left ≈
 * explorer-sized, right ≈ insights-sized.
 *
 * Each panel mounts via `CANVAS_CARD_REGISTRY[content_key]` — same
 * registry canvas placements use, so a content_key can move between
 * being a content card and a panel card without changing its
 * renderer. That's the panels-as-cards thesis: panels and content
 * cards are the same primitive at different scopes.
 *
 * Today the doctrine implementation is scoped to mode7 (web pilot).
 * Other modes still use canvas_cards. Step 5+ of the migration
 * lifts other modes onto this primitive.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { CANVAS_CARD_REGISTRY } from './card-registry';
import { CardErrorBoundary } from './card-error-boundary';
import { CardChromeProvider } from '@/lib/design/card-chrome';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import type { WebPanelPlacement } from '@/lib/api/types';


interface PanelsLayoutProps {
  panels: WebPanelPlacement[];
}

export function PanelsLayout({ panels }: PanelsLayoutProps) {
  // Bucket panels by region. top / bottom = full-width strips; the
  // body row is [left | viewport | right] — left ≈ explorer-sized,
  // right ≈ insights-sized; each is itself a card region.
  const top      = panels.filter((p) => p.region === 'top');
  const left     = panels.filter((p) => p.region === 'left');
  const viewport = panels.filter((p) => p.region === 'viewport');
  const right    = panels.filter((p) => p.region === 'right');
  const bottom   = panels.filter((p) => p.region === 'bottom');

  // Honour the schema: at most one viewport panel today (the canvas
  // container). Surface honestly if a preset accidentally declares
  // multiple — better than silently picking one.
  if (viewport.length > 1) {
    return (
      <div className="p-6 text-destructive text-sm">
        Preset declares {viewport.length} viewport panels — schema allows at most one.
      </div>
    );
  }

  const hasSides = left.length > 0 || right.length > 0;
  // Persist resize per preset — the viewport content_key is unique per
  // preset (web_reception_container_canvas, web_for_buyers_container_canvas, …).
  const autoSaveId = `panels:${viewport[0]?.content_key ?? 'shell'}`;
  // defaultSize percentages sum to 100 across the panels actually rendered.
  const viewportDefault = left.length > 0 && right.length > 0 ? 56 : right.length > 0 ? 76 : left.length > 0 ? 80 : 100;

  const viewportNodes = viewport.map((p, i) => (
    <PanelMount key={`viewport-${i}-${p.content_key}`} panel={p} />
  ));

  return (
    <div className="flex flex-col h-full w-full">
      {top.map((p, i) => (
        <PanelMount key={`top-${i}-${p.content_key}`} panel={p} />
      ))}

      {hasSides ? (
        <ResizablePanelGroup direction="horizontal" autoSaveId={autoSaveId} className="flex-1 min-h-0 w-full">
          {left.length > 0 && (
            <>
              <ResizablePanel defaultSize={20} minSize={12} collapsible className="h-full overflow-hidden">
                {left.map((p, i) => (
                  <PanelMount key={`left-${i}-${p.content_key}`} panel={p} />
                ))}
              </ResizablePanel>
              <ResizableHandle withHandle />
            </>
          )}

          <ResizablePanel defaultSize={viewportDefault} minSize={30} className="h-full overflow-hidden">
            {viewportNodes}
          </ResizablePanel>

          {right.length > 0 && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={24} minSize={14} collapsible className="h-full overflow-hidden">
                {right.map((p, i) => (
                  <PanelMount key={`right-${i}-${p.content_key}`} panel={p} />
                ))}
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      ) : (
        <div className="flex flex-1 min-h-0 w-full overflow-hidden">
          <div className="flex-1 min-w-0 h-full overflow-hidden">{viewportNodes}</div>
        </div>
      )}

      {bottom.map((p, i) => (
        <PanelMount key={`bottom-${i}-${p.content_key}`} panel={p} />
      ))}
    </div>
  );
}


function PanelMount({ panel }: { panel: WebPanelPlacement }) {
  const Component = CANVAS_CARD_REGISTRY[panel.content_key];
  if (!Component) {
    return (
      <div className="border border-dashed border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
        Unknown panel content_key: <code className="font-mono">{panel.content_key}</code>
      </div>
    );
  }
  return (
    <CardErrorBoundary contentKey={panel.content_key}>
      <CardChromeProvider contentKey={panel.content_key} bundle={panel.typography ?? null}>
        <Component />
      </CardChromeProvider>
    </CardErrorBoundary>
  );
}
